import React, { useCallback, useEffect, useState } from 'react';
import { getRouteSettlement, setRouteDeliveryFeePayer } from '../services/api';
import { friendlyError } from '../utils/friendlyError';
import { t } from '../i18n';
import { PhoneLink } from './orderKit';

const num = (v) => Number(v || 0).toFixed(2);
const etb = (v) => t('common.amount_etb', { amount: num(v) });

const box = {
  marginTop: '20px',
  padding: '16px',
  borderRadius: '14px',
  border: '1px solid #d7cdef',
  background: '#ffffff',
};

const segBtn = (active, enabled) => ({
  padding: '8px 14px',
  borderRadius: '10px',
  border: active ? '2px solid #6b4eaa' : '1px solid #d7cdef',
  background: active ? '#f3effb' : '#ffffff',
  color: '#2d1b69',
  fontWeight: active ? 700 : 500,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled || active ? 1 : 0.6,
});

/**
 * Merchant's view of a route's delivery fees: who pays the driver (whole route or per order, changeable until
 * pickup), what the merchant hands over at pickup, any driver debt paid to ODOS with the service fee, and the
 * settlement statement. `refreshKey` reloads it when the route changes (driver accepted, payment, pickup).
 * Texts share their keys with the ODOS Merchant app (`fee_panel.*`).
 */
export default function DeliveryFeePanel({ routeId, refreshKey }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showStops, setShowStops] = useState(false);

  const load = useCallback(async () => {
    if (!routeId) return;
    try {
      setS(await getRouteSettlement(routeId));
    } catch (_) {
      /* informational; the route page works without it */
    }
  }, [routeId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const setPayer = async (payer, orderIds) => {
    setSaving(true);
    setError('');
    try {
      setS(await setRouteDeliveryFeePayer(routeId, payer, orderIds));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return null;
  const total = s.senderStopCount + s.receiverStopCount;
  const selected = s.receiverStopCount === 0 ? 'SENDER' : s.senderStopCount === 0 ? 'RECEIVER' : null;
  const editable = !s.pickedUp && !saving;
  const debt = Number(s.driverDebtDeductionEtb || 0);
  const senderPays = Number(s.senderPaysEtb || 0);
  const cashAtPickup = Math.max(0, senderPays - debt);
  const stopsOpen = showStops || selected === null;

  const line = (label, amount, done) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', margin: '6px 0', fontSize: '14px' }}>
      <span>{done ? '✓ ' : ''}{label}</span>
      <strong>{etb(amount)}</strong>
    </div>
  );

  return (
    <div style={box}>
      <div style={{ fontWeight: 700, fontSize: '16px', color: '#2d1b69' }}>{t('fee_panel.title')}</div>
      <div style={{ fontSize: '12.5px', color: '#6a6480', margin: '4px 0 10px' }}>
        {s.pickedUp ? t('fee_panel.locked') : t('fee_panel.who_pays')}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button type="button" disabled={!editable} style={segBtn(selected === 'SENDER', editable)}
          onClick={() => selected !== 'SENDER' && setPayer('SENDER')}>
          {t('fee_panel.option_sender')}
        </button>
        <button type="button" disabled={!editable} style={segBtn(selected === 'RECEIVER', editable)}
          onClick={() => selected !== 'RECEIVER' && setPayer('RECEIVER')}>
          {t('fee_panel.option_receiver')}
        </button>
      </div>
      {selected === null && (
        <div style={{ fontSize: '12.5px', color: '#6a6480', marginTop: '6px' }}>
          {t('fee_panel.mixed', { sender: s.senderStopCount, total })}
        </div>
      )}

      {Array.isArray(s.stops) && s.stops.length > 1 && (
        <div style={{ marginTop: '10px' }}>
          <button type="button" onClick={() => setShowStops((v) => !v)}
            style={{ background: 'none', border: 'none', color: '#6b4eaa', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            {stopsOpen ? '▾' : '▸'} {t('fee_panel.per_stop_title')}
          </button>
          {stopsOpen && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '12.5px', color: '#6a6480', marginBottom: '6px' }}>{t('fee_panel.per_stop_hint')}</div>
              {s.stops.map((stop) => {
                const receiverPays = stop.deliveryFeePayer === 'RECEIVER';
                const address = String(stop.deliveryAddress || '').trim();
                const hasPhone = String(stop.receiverPhone || '').trim() !== '';
                return (
                  <div key={stop.orderId} style={{ borderTop: '1px solid #eee8f7', padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                      <span style={{ fontWeight: 600 }}>{t('fee_panel.stop_label', { n: stop.sequence, order: stop.orderId })}</span>
                      <strong>{etb(stop.deliveryFeeEtb)}</strong>
                    </div>
                    {(address || hasPhone) && (
                      <div style={{ fontSize: '12px', color: '#6a6480' }}>
                        {address}
                        {address && hasPhone ? ' | ' : null}
                        {hasPhone ? <PhoneLink phone={stop.receiverPhone} /> : null}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                      <button type="button" disabled={!editable || !receiverPays} style={segBtn(!receiverPays, editable)}
                        onClick={() => setPayer('SENDER', [stop.orderId])}>
                        {t('fee_panel.option_sender')}
                      </button>
                      <button type="button" disabled={!editable || receiverPays} style={segBtn(receiverPays, editable)}
                        onClick={() => setPayer('RECEIVER', [stop.orderId])}>
                        {t('fee.receiver_pays')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        {s.senderStopCount > 0 &&
          line(
            s.pickedUp && s.senderPaymentConfirmedAt ? t('fee_panel.you_paid_at_pickup') : t('fee_panel.you_pay_at_pickup'),
            debt > 0 ? cashAtPickup : senderPays,
            !!s.senderPaymentConfirmedAt
          )}
        {debt > 0 && (
          <div style={{ background: '#eef4ff', color: '#1e3a8a', borderRadius: '8px', padding: '8px 10px', fontSize: '12.5px', margin: '6px 0' }}>
            {t(s.driverDebtPaid ? 'fee_panel.debt_paid' : 'fee_panel.debt_included', {
              debt: num(debt),
              cash: num(cashAtPickup),
              full: num(senderPays),
            })}
          </div>
        )}
        {s.receiverStopCount > 0 &&
          line(t('fee_panel.receivers_pay', { count: s.receiverStopCount }), s.receiverPaysEtb,
            Number(s.receiverCollectedEtb) >= Number(s.receiverPaysEtb))}
        {s.pickedUp && s.receiverStopCount > 0 && line(t('fee_panel.receivers_collected'), s.receiverCollectedEtb)}
        {Number(s.detourExtrasEtb) > 0 && line(t('fee_panel.detour_extras'), s.detourExtrasEtb)}
        {s.pickedUp && Number(s.outstandingEtb) > 0 && line(t('fee_panel.outstanding'), s.outstandingEtb)}
      </div>
      {error && <div style={{ color: '#a63a3a', fontSize: '13px', marginTop: '6px' }}>{error}</div>}
      {saving && <div style={{ fontSize: '12px', color: '#6a6480', marginTop: '6px' }}>{t('store.saving')}</div>}
    </div>
  );
}

/**
 * Asks the merchant to confirm with the driver before paying a service fee that includes the driver's debt.
 * Render while `open`; calls onConfirm or onCancel.
 */
export function DriverDebtConfirmModal({ open, debtEtb, senderCashEtb, driverPhone, onConfirm, onCancel }) {
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);
  if (!open) return null;
  const amounts = {
    debt: num(debtEtb),
    cash: num(Math.max(0, Number(senderCashEtb || 0) - Number(debtEtb || 0))),
    full: num(senderCashEtb),
  };
  return (
    <div role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,40,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '20px', maxWidth: '440px', width: '100%' }}>
        <div style={{ fontWeight: 700, fontSize: '17px', color: '#2d1b69', marginBottom: '8px' }}>{t('fee_panel.debt_warning_title')}</div>
        <p style={{ fontSize: '14px', margin: '0 0 8px' }}>{t('fee_panel.debt_warning_body', amounts)}</p>
        {driverPhone && (
          <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 8px' }}>
            {t('fee_panel.debt_warning_driver_phone', { phone: driverPhone })}
          </p>
        )}
        <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '14px', margin: '10px 0' }}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ marginTop: '3px' }} />
          <span>{t('fee_panel.debt_warning_checkbox', amounts)}</span>
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <button type="button" onClick={onCancel}
            style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #d7cdef', background: '#ffffff', cursor: 'pointer' }}>
            {t('fee_panel.debt_warning_cancel')}
          </button>
          <button type="button" disabled={!checked} onClick={onConfirm}
            style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', background: checked ? '#6b4eaa' : '#a59eae', color: '#ffffff', fontWeight: 600, cursor: checked ? 'pointer' : 'not-allowed' }}>
            {t('fee_panel.debt_warning_continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
